import { useState, useEffect, useRef } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useProductsQuery } from "@/modules/products/services/useProductsQuery";
import type { SellerProductListItemView } from "@/lib/api/types";

type Props = {
  storeSlug: string;
  selectedIds: Set<string>;
  totalItemCount: number;
  onAdd: (product: SellerProductListItemView) => void;
};

const MAX_ITEMS = 20;

export function CheckoutLinkProductPicker({ storeSlug, selectedIds, totalItemCount, onAdd }: Props) {
  const t = useTranslations();
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setDebouncedSearch(inputValue.trim().length >= 2 ? inputValue.trim() : "");
    }, 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [inputValue]);

  const { data, isLoading } = useProductsQuery({
    storeSlug,
    search: debouncedSearch || undefined,
    limit: 10,
    offset: 0,
    enabled: debouncedSearch.length >= 2,
  } as Parameters<typeof useProductsQuery>[0] & { enabled?: boolean });

  const results = data?.items ?? [];

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={t(keys.checkoutLinks.searchPlaceholder)}
          className="h-11 w-full rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-sm text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--primary-500)]"
        />
        {debouncedSearch.length >= 2 && (
          <div className="absolute inset-x-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[0_12px_32px_rgba(15,20,40,0.18)]">
          {isLoading && (
            <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
              {t(keys.common.loading)}
            </div>
          )}
          {!isLoading && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
              {t(keys.common.empty)}
            </div>
          )}
          {results.map((product, index) => {
            const alreadySelected = selectedIds.has(product.id);
            const atCap = totalItemCount >= MAX_ITEMS;
            const disabled = alreadySelected || atCap;
            return (
              <div key={product.id}>
                {index > 0 && <div className="mx-3 border-t border-[color:var(--border-light)]" />}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onAdd(product)}
                  className="flex w-full min-w-0 items-center gap-3 px-4 py-3 text-start transition-colors active:bg-[color:var(--background-muted)] disabled:opacity-40"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-[color:var(--text-primary)]">
                    {product.nameAr}
                    {product.nameEn ? (
                      <span className="ml-1 text-xs text-[color:var(--text-muted)]">({product.nameEn})</span>
                    ) : null}
                  </span>
                  {alreadySelected && (
                    <span className="text-xs text-[color:var(--primary-500)]">{"✓"}</span>
                  )}
                </button>
              </div>
            );
          })}
          </div>
        )}
      </div>
      {totalItemCount >= MAX_ITEMS && (
        <p className="text-xs text-[color:var(--status-error)]">
          {t(keys.checkoutLinks.maxItemsError)}
        </p>
      )}
    </div>
  );
}
